import {
  COLORS,
  Z_INDEXES,
  useIsomorphicLayoutEffect,
  useTimedOut,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, useRef, useState} from 'react';
import {SEARCH_BAR_ANIMATION_TIME_MS} from '../constants';

interface SearchBarResultsProps {
  children: React.ReactNode;
  animateOnEntry?: boolean;
  /**
   * Identifies the panel that is currently rendered. Changing it fades the new
   * panel in inside the open flyout instead of tearing the flyout down.
   */
  contentKey?: string;
}

const MIN_FLYOUT_SIZE_WITH_ONE_OPTION = 37;
const ESTIMATED_HEADER_HEIGHT = 190;
const FLYOUT_MAX_HEIGHT = `calc(100vh - ${ESTIMATED_HEADER_HEIGHT}px)`;
const CONTENT_FADE_TIME_MS = 150;
// A panel that renders nothing holds the flyout open for this long, so a short
// gap - a lazy chunk, an in flight suggestions request - does not read as the
// flyout closing and reopening.
const EMPTY_CONTENT_GRACE_MS = 300;

export const SearchBarResults: FC<SearchBarResultsProps> = ({
  children,
  animateOnEntry,
  contentKey,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const lastFilledHeight = useRef<number | undefined>(undefined);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );

  useIsomorphicLayoutEffect(() => {
    const content = contentRef.current;

    if (!content || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      // Reading offsetHeight here would force a synchronous layout.
      const height =
        entry?.borderBoxSize?.[0]?.blockSize ?? content.offsetHeight;

      if (height > 0) {
        lastFilledHeight.current = height;
      }

      setContentHeight(height);
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, []);

  // The bar itself animates while it expands, so the flyout waits it out
  // instead of growing along with it.
  const isEntryDelayOver = useTimedOut(
    animateOnEntry ? SEARCH_BAR_ANIMATION_TIME_MS : null,
  );
  const isEntryPending = Boolean(animateOnEntry) && !isEntryDelayOver;

  const isEmpty = contentHeight === 0;
  const hasGracePeriodElapsed = useTimedOut(
    isEmpty ? EMPTY_CONTENT_GRACE_MS : null,
  );
  const isCollapsed =
    isEmpty &&
    (lastFilledHeight.current === undefined || hasGracePeriodElapsed);

  let height = contentHeight;
  if (isCollapsed) {
    height = 0;
  } else if (isEmpty) {
    // While the incoming panel is still empty the flyout keeps the height it
    // had, so the swap does not read as a close.
    height = lastFilledHeight.current;
  }

  return (
    <FlyoutWrapper
      data-animate-on-entry={animateOnEntry}
      data-entry-pending={isEntryPending}
      data-collapsed={isCollapsed}
      style={height === undefined ? undefined : {height: `${height}px`}}
      tabIndex={-1}
    >
      <ContentMeasure ref={contentRef}>
        <ContentLayer key={contentKey}>{children}</ContentLayer>
      </ContentMeasure>
    </FlyoutWrapper>
  );
};

const FlyoutWrapper = styled.div`
  position: absolute;
  z-index: ${Z_INDEXES.TADARIDA_HEADER_SEARCH_RESULTS};
  top: 100%;
  left: -1px;
  width: 100.5%;
  margin-top: 2px;
  background-color: ${COLORS.White};
  border: 1px solid ${COLORS.Black};
  border-radius: 4px 4px 2px 2px;
  min-height: ${MIN_FLYOUT_SIZE_WITH_ONE_OPTION}px;
  max-height: ${FLYOUT_MAX_HEIGHT};
  overflow: auto;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: height ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in-out;

  &[data-entry-pending='true'],
  &[data-collapsed='true'] {
    visibility: hidden;
    opacity: 0;
    min-height: 0;
    border-color: transparent;
    pointer-events: none;
  }

  &[data-animate-on-entry='true'][data-entry-pending='false'] {
    /* Do NOT use transpile here, linaria doesn't compile correctly the time on re render, making the animation style to break */
    animation: animateEntry 150ms ease-out forwards;

    @keyframes animateEntry {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }
  }
`;

const ContentMeasure = styled.div`
  width: 100%;

  // While the panel is still a placeholder it claims the whole flyout, so the
  // flyout opens at its full height and settles down onto the real content in
  // one step, instead of stepping up as each part of the panel arrives.
  &:has([data-panel-placeholder]) {
    min-height: ${FLYOUT_MAX_HEIGHT};
  }
`;

const ContentLayer = styled.div`
  animation: fadeInContent ${CONTENT_FADE_TIME_MS}ms ease-in;

  @keyframes fadeInContent {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;
